import React from 'react';
import { normalizeMainTheme, parseTypologyTag } from '../data/taxonomies.js';

// ============================================================
// Directory hero v2 — placeholder copy, stat strip, 3 featured cards
// ============================================================

export function pickFeaturedOrgs(allOrgs, n = 3) {
  // Score by completeness; pick top N but diverse across countries.
  const scored = allOrgs.map(o => {
    let score = 0;
    if ((o.mission || '').length > 200) score += 4;
    else if ((o.mission || '').length > 80) score += 2;
    if ((o.activities || []).length >= 2) score += 2;
    if ((o.themes || []).length >= 2) score += 2;
    if ((o.stakeholders || []).length >= 2) score += 1;
    if ((o.policyLevels || []).length) score += 1;
    if ((o.implementationLevels || []).length) score += 1;
    if (o.foundedYear) score += 0.5;
    if (o.website) score += 0.5;
    return { org: o, score };
  }).sort((a, b) => b.score - a.score || a.org.name.localeCompare(b.org.name));

  // Greedy: pick highest score from each country before going back
  const picked = [];
  const seenCountry = new Set();
  for (const { org } of scored) {
    if (picked.length >= n) break;
    if (seenCountry.has(org.country)) continue;
    picked.push(org);
    seenCountry.add(org.country);
  }
  // Fill remainder if we ran out of unique countries
  if (picked.length < n) {
    for (const { org } of scored) {
      if (picked.length >= n) break;
      if (!picked.includes(org)) picked.push(org);
    }
  }
  return picked;
}

export function DirectoryHeroV2({ orgs, onNavigate, onSelectOrg }) {
  const stats = React.useMemo(() => {
    const countries = new Set(orgs.map(o => o.country).filter(Boolean));
    const themes = new Set();
    const activities = new Set();
    for (const o of orgs) {
      for (const t of (o.themes || [])) {
        const p = parseTypologyTag(t);
        if (p?.main) themes.add(normalizeMainTheme(p.main));
      }
      for (const a of (o.activities || [])) {
        const p = parseTypologyTag(a);
        if (p?.main) activities.add(p.main);
      }
    }
    return {
      total: orgs.length,
      countries: countries.size,
      activities: activities.size,
      themes: themes.size,
    };
  }, [orgs]);

  const featured = React.useMemo(() => pickFeaturedOrgs(orgs, 3), [orgs]);

  return (
    <section className="directory-hero-v2">
      <div className="dhv2-inner">
        <div className="dhv2-headline">
          <h1 className="dhv2-title">
            {/* PLACEHOLDER COPY — replace as needed */}
            A living atlas of Europe's new economy.
          </h1>
          <p className="dhv2-lede">
            {/* PLACEHOLDER COPY — replace as needed */}
            {stats.total.toLocaleString()} organisations across {stats.countries} countries
            building new economic systems in practice.
            Browse by what they do, what they work on, or where they are.
          </p>
          <div className="dhv2-actions">
            <a className="btn-primary" href="#/submit" onClick={(e) => { e.preventDefault(); onNavigate('submit'); }}>
              Add your organisation →
            </a>
            <a className="btn-secondary dhv2-about-btn" href="#/about" onClick={(e) => { e.preventDefault(); onNavigate('about'); }}>
              About this directory
            </a>
          </div>
        </div>

        <div className="dhv2-stats">
          <div className="dhv2-stat">
            <div className="dhv2-stat-n">{stats.total.toLocaleString()}</div>
            <div className="dhv2-stat-l">Organisations</div>
          </div>
          <div className="dhv2-stat">
            <div className="dhv2-stat-n">{stats.countries}</div>
            <div className="dhv2-stat-l">Countries</div>
          </div>
          <div className="dhv2-stat">
            <div className="dhv2-stat-n">{stats.activities}</div>
            <div className="dhv2-stat-l">Activity types</div>
          </div>
          <div className="dhv2-stat">
            <div className="dhv2-stat-n">{stats.themes}</div>
            <div className="dhv2-stat-l">Thematic areas</div>
          </div>
        </div>

      </div>
    </section>
  );
}
